import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Loader2 } from "lucide-react";

// Vimeo embed managed for scroll performance: the player only mounts when the
// frame approaches the viewport, pauses as soon as it leaves the screen, and
// fully unmounts when far away — so off-screen videos never decode, buffer or
// hold memory while scrolling.
const VimeoEmbed = ({ vimeoId, label, background, className, testId }) => {
  const wrapRef = useRef(null);
  const iframeRef = useRef(null);
  const visibleRef = useRef(false);
  const [mounted, setMounted] = useState(false);
  const [playerReady, setPlayerReady] = useState(false);
  const poster = useVimeoPoster(vimeoId);

  const [videoId, hash] = String(vimeoId).split("?h=");
  const params = new URLSearchParams(
    background
      ? { background: "1", autoplay: "1", loop: "1", muted: "1", autopause: "1", dnt: "1" }
      : { title: "0", byline: "0", portrait: "0", dnt: "1" }
  );
  if (hash) params.set("h", hash);
  const src = `https://player.vimeo.com/video/${videoId}?${params.toString()}`;

  const post = (method) => {
    try {
      iframeRef.current?.contentWindow?.postMessage(
        JSON.stringify({ method }),
        "https://player.vimeo.com"
      );
    } catch {}
  };

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    // Mount the player ~1.5 screens before it appears; drop it when far away.
    const nearObs = new IntersectionObserver(
      ([e]) => {
        setMounted(e.isIntersecting);
        if (!e.isIntersecting) setPlayerReady(false);
      },
      { rootMargin: "1500px" }
    );
    // Pause the moment the frame scrolls off; resume when it comes back.
    const visObs = new IntersectionObserver(
      ([e]) => {
        visibleRef.current = e.isIntersecting;
        if (e.isIntersecting) {
          if (background) post("play");
        } else {
          post("pause");
        }
      },
      { threshold: 0.15 }
    );
    nearObs.observe(el);
    visObs.observe(el);
    return () => {
      nearObs.disconnect();
      visObs.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [background]);

  // If the player finishes loading while off-screen, keep it paused.
  const handleLoad = () => {
    setPlayerReady(true);
    if (!visibleRef.current) {
      setTimeout(() => post("pause"), 300);
      setTimeout(() => post("pause"), 1000);
    }
  };

  return (
    <div
      ref={wrapRef}
      data-testid={testId}
      className={`relative w-full overflow-hidden rounded-xl bg-black ring-1 ring-white/10 ${className}`}
      style={{ aspectRatio: "16 / 9" }}
    >
      {/* Instant poster while the Vimeo player spins up */}
      {poster && (
        <img
          src={poster}
          alt=""
          aria-hidden="true"
          loading="lazy"
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-700 ${
            playerReady ? "opacity-0" : "opacity-100"
          }`}
        />
      )}
      {mounted && (
        <iframe
          ref={iframeRef}
          title={label || "Video"}
          src={src}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          loading="lazy"
          frameBorder="0"
          onLoad={handleLoad}
          className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 transition-opacity duration-700 ${
            playerReady ? "opacity-100" : "opacity-0"
          } ${
            background ? "pointer-events-none h-[102%] w-[102%]" : "h-full w-full"
          }`}
        />
      )}
      {!playerReady && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-white/50" />
        </div>
      )}
    </div>
  );
};

// Fetches the Vimeo oEmbed thumbnail so the frame shows a real poster image
// instantly instead of a black box while the player initialises.
const useVimeoPoster = (vimeoId) => {
  const [poster, setPoster] = useState(null);
  useEffect(() => {
    if (!vimeoId) return;
    const [id, hash] = String(vimeoId).split("?h=");
    const target = `https://vimeo.com/${id}${hash ? `/${hash}` : ""}`;
    let cancelled = false;
    fetch(`https://vimeo.com/api/oembed.json?url=${encodeURIComponent(target)}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!cancelled && d && d.thumbnail_url) setPoster(d.thumbnail_url);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [vimeoId]);
  return poster;
};

// Clearly-bounded 16:9 video embed placeholder. Pass `vimeoId` to embed a real Vimeo player.
export const VideoPlaceholder = ({
  label = "BirdsEyeViews Photography",
  thumbnail,
  vimeoId,
  background = false,
  className = "",
  testId = "video-placeholder",
}) => {
  if (vimeoId) {
    return (
      <VimeoEmbed
        vimeoId={vimeoId}
        label={label}
        background={background}
        className={className}
        testId={testId}
      />
    );
  }
  return (
  <motion.div
    data-testid={testId}
    whileHover="hover"
    initial="rest"
    animate="rest"
    className={`group relative w-full overflow-hidden rounded-xl bg-black ring-1 ring-white/10 ${className}`}
    style={{ aspectRatio: "16 / 9" }}
  >
    {thumbnail ? (
      <>
        <motion.img
          src={thumbnail}
          alt={label}
          variants={{ rest: { scale: 1 }, hover: { scale: 1.05 } }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/20" />
      </>
    ) : (
      <div className="absolute inset-0 bg-black" />
    )}

    {/* dashed placeholder hint */}
    <div className="pointer-events-none absolute inset-3 rounded-lg border border-dashed border-white/15" />

    {/* centered play button */}
    <div className="absolute inset-0 flex items-center justify-center">
      <motion.div
        variants={{ rest: { scale: 1 }, hover: { scale: 1.12 } }}
        transition={{ type: "spring", stiffness: 300, damping: 18 }}
        className="flex h-16 w-16 items-center justify-center rounded-full bg-white/95 shadow-2xl sm:h-20 sm:w-20"
      >
        <Play className="ml-1 h-7 w-7 fill-slate-950 text-slate-950 sm:h-8 sm:w-8" />
      </motion.div>
    </div>

    {/* bottom-left label */}
    {label && (
      <div className="absolute bottom-4 left-4 flex items-center gap-2">
        <span className="font-sans text-lg font-medium tracking-tight text-white drop-shadow-md sm:text-2xl">
          {label}
        </span>
      </div>
    )}

    {/* placeholder badge */}
    <div className="absolute right-4 top-4 rounded-full bg-black/50 px-3 py-1 font-sans text-[10px] font-medium uppercase tracking-[0.2em] text-white/70 backdrop-blur-sm">
      Video · to be added
    </div>
  </motion.div>
  );
};

// Faint decorative drone-propeller swoosh line motif.
export const Swoosh = ({ className = "" }) => (
  <svg
    className={`pointer-events-none absolute ${className}`}
    viewBox="0 0 600 600"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M300 40C160 40 40 160 40 300s120 260 260 260"
      stroke="currentColor"
      strokeWidth="60"
      strokeLinecap="round"
    />
    <path
      d="M300 120c-99 0-180 81-180 180"
      stroke="currentColor"
      strokeWidth="36"
      strokeLinecap="round"
      opacity="0.6"
    />
  </svg>
);
